<template>
  <div v-if="!isEmpty(entry)">
    <div
      class="answers flex w-full flex-wrap"
      :class="[entry.metadata.two_box_mode ? 'justify-between' : 'justify-start']"
      :data-mode="questionType"
      :data-test-type="testType"
      :data-ismulti="isMultiTest ? 'true' : 'false'"
      :data-answer="correctAnswer"
    >
      <figure v-if="entry.image" class="relative w-full mb-2.5">
        <EntryImage :entry="entry" />
      </figure>
      <div class="answer-wrapper" :class="[entry.metadata.two_box_mode ? 'justify-between' : 'justify-start']">
        <div
          v-for="answer in testAnswers"
          :key="answer._id"
          class="answer answer-box"
          :class="{
            'half-size': isTwoBoxMode,
            'answer-line': !isImageMode,
          }"
          :data-key="getEntryKey(answer.key)"
          :data-segments="JSON.stringify(answer.segments)"
          :data-answer-score="answer.score"
        >
          <div class="answer-inline-wrapper"></div>
          <figure v-if="isImageMode" class="answer__image">
            <div style="padding-top: 100%"></div>
            <figure v-if="isGif(answer.image)" class="absolute inset-0">
              <video autoPlay loop muted playsInline>
                <source type="video/webm" :src="getImageUrl(answer.image, { format: 'webm' })" />
                <source type="video/mp4" :src="getImageUrl(answer.image, { format: 'mp4' })" />
                <img :src="getImageUrl(answer.image, 'gif')" />
              </video>
            </figure>
            <OImage
              v-else
              :src="getImageUrl(answer.image, { standard: true })"
              :alt="answer.title"
              loading="lazy"
              class="w-full h-full object-cover"
            />
          </figure>

          <h5 class="flex items-start">
            <OIcon class="empty block mr-2 w-6" icon="circle" />
            <OIcon class="correct hidden mr-2 w-6" icon="circle-dot" />
            {{ answer.title }}
          </h5>
          <div class="answer-bar-wrapper flex-col w-full bg-white justify-end hidden">
            <div class="percentage-text flex-grow flex justify-end items-center mr-2"></div>
            <div class="percentage-bar-wrapper flex-none flex justify-end">
              <div class="percentage-bar h-2"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- entry meta -->
    <div v-if="showResult">
      <OButton pure class="reveal-result text-link-primary text-sm py-2">{{ $t('news-page.show-result') }}</OButton>
    </div>

    <RenderHtml
      v-if="html"
      :class="[questionType === 'singular' ? 'text reveal-answer hidden' : 'text']"
      itemprop="articleBody"
      :html="html"
    ></RenderHtml>

    <template v-if="questionType === 'multiple'">
      <div class="reveal bg-gray-200 flex mt-10 py-5 sm:py-10 px-5 border-gray-300 justify-around flex-col sm:flex-row">
        <p class="text-center sm:text-left mb-4 sm:m-0">
          <span>{{ $t('news-page.test-finalize-1') }}</span>
          <br />
          <span>{{ $t('news-page.test-finalize-2') }}</span>
        </p>
        <button
          class="
            submit-reveal-result
            text-white
            rounded-md
            text-xl
            px-12
            font-bold
            hover:bg-blue-500
            bg-blue-400
            h-16
            flex
            items-center
            justify-center
          "
        >
          {{ $t('news-page.show-result') }}
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import { computed, defineComponent, ssrRef, onMounted } from '@nuxtjs/composition-api';
import { tinyEncrypt } from '@/modules/quiz/utils/quizUtils';
import { uniqueId, isEmpty, shuffle } from 'lodash';
import EntryImage from '~/modules/article/components/EntryImage';
import { getImageUrl } from '~/modules/common/utils/imageUtils';
import RenderHtml from '~/modules/common/components/RenderHtml';
import { OButton, OIcon, OImage } from '~/modules/common/components/ui';

export default defineComponent({
  name: 'EntryVariantQuiz',
  props: {
    entry: {
      type: Object,
      required: true,
    },
  },
  components: {
    EntryImage,
    RenderHtml,
    OButton,
    OImage,
    OIcon,
  },
  setup(props) {
    const testAnswers = ssrRef([]);
    const questionType = computed(() => props.entry?.metadata?.question_type);
    const testType = computed(() => props.entry?.metadata?.test_type);
    const isImageMode = computed(() => props.entry?.metadata?.imagemode);
    const isTwoBoxMode = computed(() => props.entry?.metadata?.two_box_mode);
    const isShuffled = computed(() => props.entry?.metadata?.shuffled);
    const isMultiTest = props.entry?.metadata?.multitest;

    // if data-key is empty, key's value = 1
    const getEntryKey = (key) => (key ? tinyEncrypt(key, null, props.entry) : '1');
    props.entry?.answers?.forEach((answer) => {
      answer.encryptedKey = tinyEncrypt(answer.key, props.entry?._id);
      answer._id = uniqueId('answer__');
    });

    // sonucu göster testleri için örnek 441808
    const showResult = computed(
      () => questionType.value === 'poll' || (questionType.value === 'multiple' && props.entry?.metadata?.multiple_poll_result)
    );

    const correctAnswer = computed(() =>
      questionType.value === 'singular'
        ? tinyEncrypt(tinyEncrypt(props.entry?.metadata?.correct_answer, props.entry?._id), props.entry?._id)
        : null
    );

    onMounted(() => {
      testAnswers.value = isShuffled.value ? shuffle(props.entry.answers) : props.entry.answers;
    });

    const html = computed(() => props?.entry?.text?.html);

    const isGif = (image) => image.resolutions.standard.format === 'gif';

    return {
      questionType,
      testType,
      correctAnswer,
      isImageMode,
      isTwoBoxMode,
      isMultiTest,
      showResult,
      testAnswers,
      html,
      //
      isEmpty,
      getEntryKey,
      getImageUrl,
      isGif,
    };
  },
});
</script>
<style lang="scss">
$quiz-passive: #e7f0ee;
$quiz-hover: #e2e2e2;
$quiz-active: #8ccc6f;
$quiz-wrong: #f55656;

.answer-inline-wrapper {
  position: absolute;
  z-index: 80;
  width: 100%;
  background-color: rgba(65, 131, 196, 0.7);
  height: auto;
  margin: 0 !important;
  color: var(--white);

  &.max {
    background-color: rgba(217, 63, 63, 0.7);
  }

  .result-text {
    display: block;
    height: 32px;
    width: 100%;
    padding: 5px;
  }
}

.answer-bar-wrapper {
  height: 43px;
  border: 1px solid #e7f0ee;
  color: var(--text-primary-100);
  margin: 0 !important;

  .percentage-bar-wrapper {
    width: 100%;
    background-color: #e7f0ee;
    margin-left: 0 !important;

    .percentage-bar {
      background-color: rgba(65, 131, 196, 0.7);
      &.max {
        background-color: rgba(217, 63, 63, 0.7);
      }
    }
  }
}

.answer-wrapper {
  display: inline-flex;
  flex-wrap: wrap;
  margin: 0 0 0 -10px;
  width: calc(100% + 10px);

  > div {
    margin: 0 0 0 10px;
  }
}

.article .entry--question {
  .answer-box {
    cursor: pointer;
    width: 45%;
  }
  .answer {
    background-color: $quiz-passive;
    margin-bottom: 10px;
    position: relative;

    &:hover {
      background-color: $quiz-hover;
    }

    &__image {
      position: relative;

      &:before {
        content: '';
        display: block;
      }

      > img {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
      }
      video {
        object-fit: cover;
        height: 100% !important;
        width: 100% !important;
      }
    }

    &.half-size {
      width: 46%;
    }

    &.full-size {
      // width: 100%;
    }

    // Desktop no-image answers
    &.answer-line,
    .answer-box {
      width: 100%;
      > h5 {
        padding: 12px;
        > span {
          //margin-top: 2px;
        }
      }
    }

    &.correct {
      background-color: $quiz-active;
      color: white;
    }

    &.wrong {
      background-color: $quiz-wrong;
      color: white;
    }

    > h5 {
      padding: 15px;
      font-size: 15px;
      line-height: 1.28;
      white-space: normal;
      word-break: break-word;
      overflow: hidden;
      text-overflow: ellipsis;

      > span {
        font-size: 20px !important;
      }
    }
  }
}

// Buranın tam olarak ne işlev gördüğünü anlamadım
article.mobile {
  .entry--question {
    .answer {
      &.half-size {
        width: 49%;
      }
      &.answer-line {
        > h5 {
          padding: 4px 10px;
          min-height: 30px;
          font-size: 16px;
          white-space: normal;
          word-break: break-word;
          overflow: hidden;
          text-overflow: ellipsis;
          line-height: 28px;
        }
      }
      > h5 {
        padding: 4px 10px;
        min-height: 30px;
        font-size: 16px;
        white-space: normal;
        word-break: break-word;
        overflow: hidden;
        text-overflow: ellipsis;
        line-height: 28px;
      }
    }
  }
}

// burasi jquery ile eklenen paylas butonlari ile alakali
// refactorden sonra silinebilir

.share-test-result {
  h4 {
    margin-bottom: 20px;
    font-size: 22px;
  }
}

a.share-res {
  margin-bottom: 10px;
  align-items: center;
  justify-content: center;
  width: 190px;
  padding: 10px 5px;
  border-radius: 5px;
  text-align: center;
  font-size: 15px;
  font-weight: 400;
  color: #fff;
  text-decoration: none;
  vertical-align: middle;
  svg {
    margin-right: 10px;
    width: 22px;
    height: 22px;
  }

  &.fb {
    background-repeat: repeat-x;
    margin-right: 5px;
    margin-left: 5px;
    background-color: #6e90c0;
    background-image: linear-gradient(to left, #4974b0, #6e90c0);

    &:hover {
      background: #466ba0;
    }
  }

  &.tw {
    background-repeat: repeat-x;
    background-color: #5daae6;
    background-image: linear-gradient(to left, #4e9edc, #5daae6);
    margin-right: 5px;
    margin-left: 5px;

    i {
      font-size: 22px;
      line-height: 22px;
    }

    &:hover {
      background: #3191dc;
    }
  }

  &.wsp {
    background: #2ab200;
    margin-right: 5px;
    margin-left: 5px;

    i {
      &.wsp-icon {
        // bgimage-data('logo/whatsapp', 'svg');
        background-size: 23px;
        background-repeat: no-repeat;
        background-position: center center;
        height: 23px;
        width: 23px;
        display: inline-block;
        margin-right: 8px;
      }
    }

    &:hover {
      background: #2ab200;
    }
  }
}

@media all and (min-width: 1024px) {
  .article {
    .entry--question {
      .answer-box {
        width: 31.44444444%;
      }
      .answer {
        &.half-size {
          width: 48%;
        }
        > h5 {
          font-size: 18px;
        }
      }
    }
  }
}
</style>
